import { useState } from "react";
import { Collapse, Paper, Text } from "@mantine/core";
import { IconChevronRight } from "@tabler/icons-react";
import { ActionRow, SelectAllRow, SortBar, FieldGrid, ListStatus, RowCheckbox, tintStyle } from "./parts";
import type { MobileView } from "./types";

/** Compact list: one line per row (title, subtitle, badge, chevron); tap opens details + actions in place. */
export function CompactList<T>({ view, stagger = true }: { view: MobileView<T>; stagger?: boolean }) {
  const [open, setOpen] = useState<Set<string | number>>(() => new Set());
  const status = <ListStatus view={view} />;
  if (view.loading || view.rows.length === 0) return status;

  const toggle = (k: string | number) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });

  return (
    <>
    <SortBar view={view} />
    <Paper withBorder radius="md" className={stagger ? "app-rt-compact app-stagger" : "app-rt-compact"}>
      <SelectAllRow view={view} />
      {view.rows.map((row) => {
        const k = view.rowKey(row);
        const isOpen = view.alwaysOpen || open.has(k);
        const badge = view.badge?.(row);
        const subtitle = view.subtitle?.(row);
        const head = (
          <>
            <RowCheckbox view={view} row={row} />
            <div className="app-rt-titles">
              <Text fw={600} size="sm" lh={1.3} className="app-rt-title">{view.title(row)}</Text>
              {subtitle != null && subtitle !== false && (
                <Text size="xs" c="dimmed" lh={1.3} className="app-rt-title">{subtitle}</Text>
              )}
            </div>
            {badge != null && badge !== false && <div className="app-rt-badge">{badge}</div>}
            {!view.alwaysOpen && (
              <IconChevronRight size={18} className="app-rt-chev" data-open={isOpen || undefined} aria-hidden />
            )}
          </>
        );
        return (
          <div key={k} className="app-rt-row" style={tintStyle(view.rowBg?.(row))}>
            {view.alwaysOpen ? (
              <div className="app-rt-head">{head}</div>
            ) : (
              <div
                className="app-rt-head app-press"
                role="button"
                tabIndex={0}
                aria-expanded={isOpen}
                onClick={() => toggle(k)}
                onKeyDown={(e) => {
                  if (e.target !== e.currentTarget) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggle(k);
                  }
                }}
              >
                {head}
              </div>
            )}
            <Collapse in={isOpen} transitionDuration={180}>
              <div className="app-rt-body">
                <FieldGrid view={view} row={row} />
                <ActionRow view={view} row={row} />
              </div>
            </Collapse>
          </div>
        );
      })}
    </Paper>
    </>
  );
}
